import PropTypes from 'prop-types';
import { Spin } from 'antd';
import { CameraOutlined, EditOutlined } from '@ant-design/icons';
import { Container } from '@/components/ui/Container';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import StatusBadge from '@/components/ui/StatusBadge';
import ProfileCopyableIdsBlock from '@/components/module/profile/ProfileCopyableIdsBlock';
import {
    ContentCardHeader,
    ContentCardTitle,
    ContentCardRow,
    ContentCardFooter,
    DetailLabel,
    DetailValueFlex,
    ProfileAccountCard,
    ProfileAccountCardBody,
    ProfileAccountCardSpacer,
    ProfileIdentityRow,
    ProfileAvatarButton,
    ProfileAvatarImage,
    ProfileAvatarOverlay,
    ProfileIdentityMeta,
    ProfileDisplayName,
    ProfileMetaLine,
} from '@/pages/inside/tenant/style';

const ProfileIdentityCard = ({
    profile,
    isEditing,
    draft,
    onDraftChange,
    onStartEdit,
    onCancelEdit,
    onSave,
    profileSaving,
    avatarInputRef,
    avatarUploading,
    onOpenAvatarPicker,
    onAvatarFileChange,
    onCopyId,
}) => {
    const {
        name,
        email,
        companyName,
        phone,
        verified,
        isOnboarded,
        companyId,
        publicId,
        avatarUrl,
        avatarBg,
        avatarCacheKey,
        initials,
    } = profile;

    return (
        <ProfileAccountCard>
            <ContentCardHeader style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <ContentCardTitle>Account</ContentCardTitle>
                {!isEditing ? (
                    <Button
                        type="default"
                        $primary={false}
                        $size="14px"
                        $padding="6px 14px"
                        onClick={onStartEdit}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                    >
                        <EditOutlined /> Edit
                    </Button>
                ) : null}
            </ContentCardHeader>

            <ProfileAccountCardBody>
            <ProfileIdentityRow>
                <ProfileAvatarButton
                    type="button"
                    onClick={onOpenAvatarPicker}
                    disabled={avatarUploading}
                    aria-label="Change profile photo"
                >
                    <ProfileAvatarImage $bg={avatarBg.bg}>
                        {avatarUrl ? (
                            <img
                                key={avatarCacheKey || avatarUrl}
                                src={avatarUrl}
                                alt={name}
                            />
                        ) : (
                            initials
                        )}
                    </ProfileAvatarImage>
                    <ProfileAvatarOverlay className="profile-avatar-overlay">
                        {avatarUploading ? <Spin size="small" /> : <CameraOutlined />}
                    </ProfileAvatarOverlay>
                </ProfileAvatarButton>
                <input
                    ref={avatarInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
                    style={{ display: 'none' }}
                    onChange={onAvatarFileChange}
                />

                <ProfileIdentityMeta>
                    <ProfileDisplayName>{isEditing ? draft.name || name : name}</ProfileDisplayName>
                    <ProfileMetaLine>{email}</ProfileMetaLine>
                    {companyName ? <ProfileMetaLine>{companyName}</ProfileMetaLine> : null}
                    <Container $display="flex" $gap="8px" $flexWrap="wrap" $marginTop="8px" $alignItems="center">
                        <StatusBadge status={verified ? 'active' : 'inactive'} size="small">
                            {verified ? 'Verified' : 'Unverified'}
                        </StatusBadge>
                        <StatusBadge status={isOnboarded ? 'active' : 'inactive'} size="small">
                            {isOnboarded ? 'Onboarded' : 'Not onboarded'}
                        </StatusBadge>
                    </Container>
                </ProfileIdentityMeta>
            </ProfileIdentityRow>

            <ContentCardRow>
                <DetailLabel>Name</DetailLabel>
                <DetailValueFlex>
                    {isEditing ? (
                        <Input
                            value={draft.name}
                            onChange={(e) => onDraftChange('name', e.target.value)}
                            $maxWidth="100%"
                        />
                    ) : (
                        <Input $readOnly value={name} $maxWidth="100%" />
                    )}
                </DetailValueFlex>
            </ContentCardRow>

            <ContentCardRow>
                <DetailLabel>Email</DetailLabel>
                <DetailValueFlex>
                    {isEditing ? (
                        <Input
                            value={draft.email}
                            onChange={(e) => onDraftChange('email', e.target.value)}
                            $maxWidth="100%"
                        />
                    ) : (
                        <Input $readOnly value={email} $maxWidth="100%" />
                    )}
                </DetailValueFlex>
            </ContentCardRow>

            <ContentCardRow>
                <DetailLabel>Phone</DetailLabel>
                <DetailValueFlex>
                    {isEditing ? (
                        <Input
                            value={draft.phone}
                            onChange={(e) => onDraftChange('phone', e.target.value)}
                            placeholder="Optional"
                            $maxWidth="100%"
                        />
                    ) : (
                        <Input $readOnly value={phone || '—'} $maxWidth="100%" />
                    )}
                </DetailValueFlex>
            </ContentCardRow>

            {companyName ? (
                <ContentCardRow>
                    <DetailLabel>Company</DetailLabel>
                    <DetailValueFlex>
                        <Input $readOnly value={companyName} $maxWidth="100%" />
                    </DetailValueFlex>
                </ContentCardRow>
            ) : null}

            <ProfileCopyableIdsBlock
                companyId={companyId}
                publicId={publicId}
                onCopy={onCopyId}
            />

            {!isEditing ? <ProfileAccountCardSpacer aria-hidden /> : null}

            {isEditing ? (
                <ContentCardFooter>
                    <Button type="default" $primary={false} onClick={onCancelEdit} disabled={profileSaving}>
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        $primary
                        $size="14px"
                        $padding="8px 20px"
                        onClick={onSave}
                        disabled={profileSaving}
                    >
                        {profileSaving ? 'Saving…' : 'Save changes'}
                    </Button>
                </ContentCardFooter>
            ) : null}
            </ProfileAccountCardBody>
        </ProfileAccountCard>
    );
};

ProfileIdentityCard.propTypes = {
    profile: PropTypes.object.isRequired,
    isEditing: PropTypes.bool.isRequired,
    draft: PropTypes.object.isRequired,
    onDraftChange: PropTypes.func.isRequired,
    onStartEdit: PropTypes.func.isRequired,
    onCancelEdit: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    profileSaving: PropTypes.bool.isRequired,
    avatarInputRef: PropTypes.object,
    avatarUploading: PropTypes.bool.isRequired,
    onOpenAvatarPicker: PropTypes.func.isRequired,
    onAvatarFileChange: PropTypes.func.isRequired,
    onCopyId: PropTypes.func.isRequired,
};

export default ProfileIdentityCard;
